import { useMemo } from 'react'

// import { Global } from '@emotion/react'

import hljs from 'highlight.js/lib/core'
import SafeHTML from '../safe-html'

import bash from 'highlight.js/lib/languages/bash'
import csharp from 'highlight.js/lib/languages/csharp'
import cpp from 'highlight.js/lib/languages/cpp'
import go from 'highlight.js/lib/languages/go'
import java from 'highlight.js/lib/languages/java'
import javascript from 'highlight.js/lib/languages/javascript'
import plaintext from 'highlight.js/lib/languages/plaintext'
import php from 'highlight.js/lib/languages/php'
import python from 'highlight.js/lib/languages/python'
import ruby from 'highlight.js/lib/languages/ruby'
import rust from 'highlight.js/lib/languages/rust'
import xml from 'highlight.js/lib/languages/xml'
import yaml from 'highlight.js/lib/languages/yaml'
import { isPrint } from '@/utils/print'

import CopyButton from '@/components/copy-button'
import { cx } from '@/utils/strings'
import { TypeComponentCode } from '@/types/ctf'

import './code.css'

export type CodeProps = TypeComponentCode<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
export default function Code({ body, language: langStr = 'Bash', wordWrap }: CodeProps) {
  const isPrintView = isPrint()
  const highlightedCode = useMemo(() => {
    const languageMap = {
      Bash: {
        key: 'bash',
        file: bash,
        extraKeywords: ['bw', 'bws', 'alias', 'chmod', 'echo', 'export', 'npm', 'yarn'],
      },
      'C++': {
        key: 'cpp',
        file: cpp,
      },
      'C#': {
        key: 'csharp',
        file: csharp,
      },
      Go: {
        key: 'go',
        file: go,
      },
      HTML: {
        key: 'xml',
        file: xml,
      },
      Java: {
        key: 'java',
        file: java,
      },
      JavaScript: {
        key: 'javascript',
        file: javascript,
      },
      'Plain Text': {
        key: 'plaintext',
        file: plaintext,
      },
      PHP: {
        key: 'php',
        file: php,
      },
      Python: {
        key: 'python',
        file: python,
      },
      Ruby: {
        key: 'ruby',
        file: ruby,
      },
      Rust: {
        key: 'rust',
        file: rust,
      },
      XML: {
        key: 'xml',
        file: xml,
      },
      YAML: {
        key: 'yaml',
        file: yaml,
      },
    }

    let language
    if (Object.hasOwn(languageMap, langStr)) {
      language = languageMap[langStr]
    } else {
      // Fallback to matching by key (case insensitive)
      const values = Object.values(languageMap)
      language = values.find((lang) => lang.key.toLowerCase() === langStr.toLowerCase())
    }
    if (!language) {
      language = languageMap['Bash']
    }

    hljs.registerLanguage(language.key, language.file)
    if ('extraKeywords' in language) {
      const hljsLanguage = hljs.getLanguage(language.key)
      if (hljsLanguage) {
        hljsLanguage.keywords += ` ${(language as { extraKeywords: string[] }).extraKeywords.join(' ')}`
      }
    }

    return hljs.highlight(body, { language: language.key }).value
  }, [body, langStr])

  return (
    <div className={cx('mb-4 relative w-full', isPrintView && 'break-inside-avoid')}>
      <pre className="flex flex-col text-primary-700 dark:text-white leading-[2] dark:bg-secondary-900 bg-white rounded-2xl overflow-hidden group border border-secondary-200 dark:border-secondary-400">
        <div className="flex-[1 0 auto] bg-secondary-100 dark:bg-secondary-800 border-b-[1px] dark:border-[#545E66] px-4 pt-2 pb-1 flex justify-between items-center">
          <span className={cx(isPrintView ? 'text-[8pt]' : 'text-xs', 'text-primary-900 dark:text-gray-300')}>
            {langStr}
          </span>
          {!isPrintView && (
            <CopyButton
              text={body}
              className="text-primary-900 opacity-100 dark:text-gray-300 dark:opacity-80 text-xs"
            />
          )}
        </div>

        <div className="bw-code-container flex-[1 0 auto] w-full h-full overflow-auto">
          <div className="flex pb-2 box-content">
            <SafeHTML
              as="code"
              className={cx(
                'pl-4 pr-8 pt-4 pb-2 block',
                wordWrap && 'text-wrap break-normal',
                isPrintView && 'text-pretty break-all'
              )}
              html={highlightedCode}
            />
          </div>
        </div>
      </pre>
    </div>
  )
}
